/**
 * Landing offer discounts — apply only on /pages/landing-page Buy Now.
 * On every other page, strip landing discount codes so they never leak site-wide.
 */
(function () {
  if (window.__landingPageOfferInit) return;
  window.__landingPageOfferInit = true;

  const STORAGE_KEY = 'berri_landing_discount_codes';
  const ATTR_KEY = 'landing_offer';
  const DEFAULT_CODES = ['BERRI20LP', 'FREESHIPLP'];
  const isLandingPage = document.body.classList.contains('landing-page');

  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  function getLandingCodes() {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        return stored
          .split(',')
          .map((c) => c.trim().toUpperCase())
          .filter(Boolean);
      }
    } catch (e) {}
    return DEFAULT_CODES.slice();
  }

  function persistCodes(codes) {
    try {
      sessionStorage.setItem(STORAGE_KEY, codes.join(','));
    } catch (e) {}
  }

  function clearPersistedCodes() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {}
  }

  async function fetchCart() {
    const res = await fetch('/cart.js?_=' + Date.now(), { credentials: 'same-origin' });
    return res.json();
  }

  async function updateCartAttributes(attrs) {
    await fetch((window.routes && window.routes.cart_update_url) || '/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ attributes: attrs }),
    });
  }

  async function clearDiscountSession() {
    try {
      await fetch('/checkout?discount=', {
        method: 'GET',
        credentials: 'same-origin',
        redirect: 'manual',
      });
      await sleep(120);
    } catch (e) {}
  }

  function cartHasLandingCode(cart, codes) {
    if (!cart || !cart.discount_codes || !cart.discount_codes.length) return false;
    const set = {};
    codes.forEach((c) => {
      set[c.toUpperCase()] = true;
    });
    return cart.discount_codes.some(function (d) {
      return d && d.code && set[String(d.code).toUpperCase()];
    });
  }

  function cartHasLandingAttribute(cart) {
    return !!(cart && cart.attributes && String(cart.attributes[ATTR_KEY] || '') === 'true');
  }

  async function silentRefreshCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || typeof cartDrawer.getSectionsToRender !== 'function') return;

    try {
      const res = await fetch('/?sections=cart-drawer,cart-icon-bubble&_=' + Date.now(), {
        credentials: 'same-origin',
      });
      const sections = await res.json();
      if (!sections || !sections['cart-drawer']) return;

      const wasOpen = cartDrawer.classList.contains('active');

      cartDrawer.getSectionsToRender().forEach(function (section) {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        if (!sectionElement || !sections[section.id]) return;
        sectionElement.innerHTML = cartDrawer.getSectionInnerHTML(sections[section.id], section.selector);
      });

      if (wasOpen) {
        cartDrawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', cartDrawer.close.bind(cartDrawer));
        cartDrawer.querySelector('cart-drawer-upsell-slider')?.init?.();
      }
    } catch (e) {}
  }

  /** Strip landing-only discounts/attrs when shopper is NOT on the landing page. */
  async function clearLandingOfferFromSite() {
    const codes = getLandingCodes();
    let cart;
    try {
      cart = await fetchCart();
    } catch (e) {
      return;
    }

    const hasCode = cartHasLandingCode(cart, codes);
    const hasAttr = cartHasLandingAttribute(cart);
    if (!hasCode && !hasAttr) {
      clearPersistedCodes();
      return;
    }

    if (hasCode) await clearDiscountSession();
    if (hasAttr) await updateCartAttributes({ [ATTR_KEY]: '' });
    clearPersistedCodes();
    await silentRefreshCartDrawer();
  }

  /* -------------------- Non-landing pages: always clear -------------------- */
  if (!isLandingPage) {
    clearLandingOfferFromSite().catch(function () {});
    return;
  }

  /* -------------------- Landing page Buy Now flow -------------------- */
  const configEl = document.getElementById('LandingOfferConfig');
  if (!configEl) return;

  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    return;
  }

  const codes = (config.codes || DEFAULT_CODES)
    .map((c) => String(c || '').trim().toUpperCase())
    .filter(Boolean);
  const variantId = String(config.variantId || '').trim();
  const properties = config.properties || {};

  if (!codes.length || !variantId) return;

  async function applyCode(code) {
    await fetch('/discount/' + encodeURIComponent(code), {
      method: 'GET',
      credentials: 'same-origin',
      redirect: 'manual',
    });
    await sleep(150);
  }

  async function applyLandingDiscounts() {
    for (let i = 0; i < codes.length; i++) {
      await applyCode(codes[i]);
    }
    persistCodes(codes);
    await updateCartAttributes({ [ATTR_KEY]: 'true' });
    return fetchCart();
  }

  function getCartDrawer() {
    return document.querySelector('cart-drawer');
  }

  async function refreshAndOpenDrawer() {
    const cartDrawer = getCartDrawer();
    if (!cartDrawer || typeof cartDrawer.renderContents !== 'function') return false;

    cartDrawer.classList.remove('is-empty');

    const res = await fetch('/?sections=cart-drawer,cart-icon-bubble&_=' + Date.now(), {
      credentials: 'same-origin',
    });
    const sections = await res.json();

    if (!sections || !sections['cart-drawer']) {
      console.error('[landing-page-offer] Missing cart-drawer section HTML');
      return false;
    }

    cartDrawer.renderContents({ sections });
    return true;
  }

  async function addVariantToCart() {
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    Object.keys(properties).forEach(function (key) {
      formData.append('properties[' + key + ']', properties[key]);
    });

    const res = await fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: formData,
    });

    const data = await res.json();
    if (data.status && data.message) {
      throw new Error(data.description || data.message || 'Could not add to cart');
    }
    return data;
  }

  let buyInFlight = false;

  function setBuyButtonsLoading(isLoading) {
    const buttons = document.querySelectorAll('[data-landing-buy], .landing-page .multirow__footer-button');
    buttons.forEach((btn) => {
      if (isLoading) {
        if (!btn.dataset.originalLabel) {
          btn.dataset.originalLabel = (btn.innerHTML || '').trim();
        }
        btn.setAttribute('aria-busy', 'true');
        btn.classList.add('is-loading');
        btn.textContent = 'Adding...';
      } else {
        btn.removeAttribute('aria-busy');
        btn.classList.remove('is-loading');
        if (btn.dataset.originalLabel) {
          btn.innerHTML = btn.dataset.originalLabel;
        }
      }
    });
  }

  async function handleLandingBuy() {
    if (buyInFlight) return;
    buyInFlight = true;
    setBuyButtonsLoading(true);

    try {
      await applyLandingDiscounts();
      const response = await addVariantToCart();
      await applyLandingDiscounts();

      const cartDrawer = getCartDrawer();
      if (cartDrawer) cartDrawer.classList.remove('is-empty');

      const opened = await refreshAndOpenDrawer();
      if (!opened) {
        window.location.href =
          ((window.routes && window.routes.cart_url) || '/cart') +
          '?discount=' +
          encodeURIComponent(codes.join(','));
        return;
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'landing-page-offer',
          productVariantId: variantId,
          cartData: response,
        });
      }
    } catch (err) {
      console.error('[landing-page-offer]', err);
      window.alert(err.message || 'Something went wrong adding this offer to your cart.');
    } finally {
      buyInFlight = false;
      setBuyButtonsLoading(false);
    }
  }

  function isLandingBuyTarget(el) {
    if (!el) return false;
    if (el.closest('[data-landing-buy]')) return true;
    if (el.closest('.landing-page .multirow__footer-button')) return true;
    const anchor = el.closest('a[href]');
    if (!anchor) return false;
    const href = anchor.getAttribute('href') || '';
    return href.indexOf('#landing-buy') !== -1;
  }

  document.addEventListener(
    'click',
    function (evt) {
      if (!isLandingBuyTarget(evt.target)) return;
      evt.preventDefault();
      handleLandingBuy();
    },
    true
  );

  // Checkout from landing side cart: re-assert codes so they stick into checkout.
  document.addEventListener('click', function (evt) {
    const checkoutBtn = evt.target.closest('[name="checkout"], .cart__checkout-button');
    if (!checkoutBtn) return;
    applyLandingDiscounts().catch(function () {});
  });
})();
